import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import Section, { SectionHeader, FadeIn } from "@/components/Section";
import { SERVICES } from "@/lib/brand";

const DETAILS = {
  "azubi-coaching": {
    situations: [
      "Unsicherheit in der Ausbildung oder Orientierungslosigkeit im Beruf",
      "Schwierigkeiten mit Strukturen, Vorgesetzten oder Prüfungen",
      "Fehlende Motivation oder Identifikation mit dem Beruf",
    ],
    themes: [
      "Rolle als Azubi verstehen und ausfüllen",
      "Kommunikation mit Ausbildern, Kollegen und Kunden",
      "Selbstorganisation, Lernstrategien und Prüfungsvorbereitung",
      "Umgang mit Feedback und ersten Konflikten",
    ],
  },
  berufseinsteiger: {
    situations: [
      "Erster Job nach Ausbildung oder Studium",
      "Jobwechsel oder Neuausrichtung nach ersten Jahren",
      "Unsicherheit im Umgang mit neuen Verantwortungen",
    ],
    themes: [
      "Professionelles Auftreten von Tag eins",
      "Erwartungshaltungen klären und Erwartungen setzen",
      "Sichtbarkeit im Team und gegenüber Vorgesetzten",
      "Priorisierung und Umgang mit Komplexität",
    ],
  },
  "karriere-coaching": {
    situations: [
      "Sie wollen den nächsten Karriereschritt gezielt vorbereiten",
      "Gefühl, auf der Stelle zu treten trotz guter Leistung",
      "Unklarheit über das eigene berufliche Profil und Wert",
    ],
    themes: [
      "Positionierung und Markenbildung im Unternehmen",
      "Strategische Karriereplanung statt Zufall",
      "Gehalts- und Entwicklungsgespräche vorbereiten",
      "Profil schärfen und klar kommunizieren",
    ],
  },
  fachkraefte: {
    situations: [
      "Fachliche Exzellenz wird anerkannt, aber nicht belohnt",
      "Wunsch nach mehr Einfluss und Gestaltungsspielraum",
      "Übergang in erste Führungs- oder Leitaufgaben steht an",
    ],
    themes: [
      "Expertise sichtbar machen, ohne zu posieren",
      "Führung ohne formale Führungsrolle",
      "Komplexe Sachverhalte klar kommunizieren",
      "Interne Netzwerke strategisch aufbauen",
    ],
  },
  fuehrungskraefte: {
    situations: [
      "Erste Führungsrolle und Unsicherheit in der neuen Position",
      "Reorganisation, Restrukturierung oder Krisenphase",
      "Konflikt im Führungsteam oder mit Shareholdern",
    ],
    themes: [
      "Klarheit über Rolle, Haltung und Prioritäten",
      "Entscheidungen unter Druck treffen und vertreten",
      "Strategische Kommunikation nach oben und unten",
      "Veränderungsprozesse sicher durch Teams führen",
    ],
  },
  kommunikation: {
    situations: [
      "Wichtige Präsentationen oder Verhandlungen stehen an",
      "Wirkung im Meeting bleibt hinter dem fachlichen Beitrag zurück",
      "Schwierige Gespräche oder Konflikte brauchen Vorbereitung",
    ],
    themes: [
      "Sprache, Körper und Präsenz im Einklang",
      "Vor Gruppen und Entscheidern überzeugend auftreten",
      "Verhandlungs- und Konfliktgespräche strukturieren",
      "Storytelling und Argumentationslinien",
    ],
  },
};

export default function ServicesPage() {
  return (
    <div data-testid="services-page" className="pt-36 lg:pt-44">
      {/* Intro */}
      <Section padded={false} className="pb-20">
        <div className="max-w-4xl">
          <FadeIn>
            <div className="overline mb-6">Leistungen</div>
            <h1 className="font-display text-[44px] sm:text-[60px] lg:text-[80px] leading-[0.98] tracking-[-0.035em] text-white">
              Sechs Formate.
              <br />
              <span className="font-editorial italic text-white/90">
                Ein Anspruch: Wirksamkeit.
              </span>
            </h1>
            <p className="mt-8 text-white/65 text-lg leading-relaxed">
              Jedes Coaching beginnt mit einem klaren Ziel und endet mit einem
              sichtbaren Ergebnis. Wählen Sie das Format, das zu Ihrer
              aktuellen Phase passt — oder sprechen Sie mit mir, welches das
              richtige ist.
            </p>
          </FadeIn>
        </div>
      </Section>

      {/* Service detail sections */}
      {SERVICES.map((s, idx) => {
        const d = DETAILS[s.slug];
        const isAlt = idx % 2 === 1;
        return (
          <section
            id={s.slug}
            key={s.slug}
            data-testid={`service-detail-${s.slug}`}
            className={`py-24 lg:py-28 ${isAlt ? "bg-[#101B34]" : ""}`}
          >
            <div className="container-edge">
              <FadeIn>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                  <div className="lg:col-span-4">
                    <div className="overline mb-5">
                      Leistung · {s.code}
                    </div>
                    <h2 className="font-display text-3xl lg:text-5xl tracking-[-0.02em] text-white leading-[1.05]">
                      {s.title}
                    </h2>
                    <p className="mt-6 text-white/60 text-[16px] leading-relaxed">
                      {s.excerpt}
                    </p>
                    <Link
                      to="/kontakt"
                      data-testid={`service-cta-${s.slug}`}
                      className="btn-primary mt-8"
                    >
                      Anfragen
                      <ArrowUpRight size={18} />
                    </Link>
                  </div>

                  <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="surface p-6 md:col-span-1">
                      <div className="overline">Typische Situation</div>
                      <ul className="mt-5 space-y-3 text-white/70 text-[14.5px]">
                        {d.situations.map((x) => (
                          <li key={x} className="flex items-start gap-2">
                            <span className="mt-1.5 inline-block w-1 h-1 rounded-full bg-brand-400 flex-shrink-0" />
                            <span>{x}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="surface p-6 md:col-span-1">
                      <div className="overline">Mögliche Themen</div>
                      <ul className="mt-5 space-y-3 text-white/70 text-[14.5px]">
                        {d.themes.map((x) => (
                          <li key={x} className="flex items-start gap-2">
                            <span className="mt-1.5 inline-block w-1 h-1 rounded-full bg-white/30 flex-shrink-0" />
                            <span>{x}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="surface p-6 md:col-span-1">
                      <div className="overline">Ergebnisse</div>
                      <ul className="mt-5 space-y-3 text-white/70 text-[14.5px]">
                        {s.outcomes.map((x) => (
                          <li key={x} className="flex items-start gap-2">
                            <CheckCircle2
                              size={15}
                              className="mt-0.5 text-brand-400 flex-shrink-0"
                              strokeWidth={1.75}
                            />
                            <span>{x}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </FadeIn>
            </div>
          </section>
        );
      })}

      {/* Closing CTA */}
      <Section>
        <div className="surface p-10 lg:p-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div className="max-w-2xl">
            <div className="overline mb-4">Nächster Schritt</div>
            <h2 className="font-display text-3xl lg:text-4xl tracking-tight text-white leading-[1.1]">
              Unsicher, welches Format passt? Sprechen wir.
            </h2>
          </div>
          <Link to="/kontakt" data-testid="services-final-cta" className="btn-primary">
            Termin anfragen <ArrowUpRight size={18} />
          </Link>
        </div>
      </Section>
    </div>
  );
}
